import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Skeleton } from "antd-mobile";
import useTranslate from "@/hooks/useTranslate";
import { isShowNews1 } from "@/utils/isShowNews1";
import { getSearchParam } from "@/utils";
import { scrollToTop } from "@/utils/scrollToTop";
import { getMainDomain } from "@/utils/getMainDomain";
import bus from "@/utils/mitt";
import { useAtom } from "jotai";
import { appAtom, clickTabAtom } from "@/atoms/app";
import { homeAtom } from "@/atoms/home";
import { miQsWeatherDomains } from "@/constants/domain.config";
interface NavItem {
  path: string;
  translationKey: string;
  devOnly?: boolean;
}

interface NavigationProps {
  onRequestMqsInterstitial?: () => void;
}

const SPECIAL_DOMAINS = ["mobi321.com", "24hweather.com"];

export const fetchDataMap: Record<string, any> = {};

export const getWeatherBgUrl = (weatherBg: string | undefined): string => {
  if (!weatherBg) return "";
  const mainDomain = getMainDomain();
  const isSpecialDomain = SPECIAL_DOMAINS.includes(mainDomain);
  const baseUrl = isSpecialDomain ? window.location.origin : import.meta.env.VITE_API_ATRGET;
  const querySuffix = isDevEnv ? "?test=1" : "";
  return `${baseUrl}${weatherBg}${querySuffix}`;
};

const Navigation: React.FC<NavigationProps> = ({ onRequestMqsInterstitial }) => {
  const locInfo = LocalStorage.getItem("INFO");
  const homeData = useAtomValue(homeAtom);
  const { pathname } = useLocation();
  const [appState, setAppState] = useAtom(appAtom);
  const { hostName } = useHostName();
  const navigate = useNavigate();
  const { scrollToCenter } = useScrollToCenter();
  const { $t } = useTranslate();
  const weatherBg = homeData?.data?.info?.weatherBg || locInfo?.weatherBg;
  const appBgUrl = getWeatherBgUrl(weatherBg);
  const navItemRefs = useRef<Record<string, HTMLAnchorElement | HTMLDivElement | null>>({});
  const [_isNewsVisible, setIsNewsVisible] = useState(true);
  const [, setClickTab] = useAtom(clickTabAtom);
  const isMqsHost = miQsWeatherDomains.includes(hostName);

  bus.on("toggleNewsVisibility", (data: any) => {
    setIsNewsVisible(data);
  });

  const setThemeColor = () => {
    setAppState(prev => ({
      ...prev,
      appBgImageUrl: appBgUrl,
    }));
  };

  useEffect(() => {
    setThemeColor();
  }, [appBgUrl]);

  const bgStyle = {
    backgroundImage: `url(${appState.appBgImageUrl})`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left bottom",
  };
  const navStyle = {
    height: `50px`,
    lineHeight: `50px`,
  };

  useEffect(() => {
    const activePath = navItems.find(
      item => pathname.indexOf(item.path) === 0 || (pathname === "/home" && item.path === "/now"),
    )?.path;

    if (activePath && navItemRefs.current[activePath]) {
      const target = navItemRefs.current[activePath];
      const container = target?.parentElement as HTMLElement;
      if (target && container) {
        scrollToCenter(target, container);
      }
    }
  }, [pathname]);

  const handleClickTab = (path: string) => {
    return (e: any) => {
      const target = e.currentTarget as HTMLAnchorElement | HTMLDivElement;
      // 需求2307 将小米天气，小部件广告顶部锚定adx广告修改底部锚定广告
      /*if (top_cnchor_domains.includes(hostName)) {
        document.body.style.padding = "0px 0px 0px";
      }*/

      const container = target.parentElement as HTMLElement;
      scrollToCenter(target, container);
      if (pathname === path) {
        return;
      }
      !isDevEnv && gtag("event", "clickNavi", { navi: path });
      if (isMqsHost) {
        onRequestMqsInterstitial?.();
        setClickTab(true);
        scrollToTop();
        return;
      }

      setClickTab(true);
      navigate(path, {
        replace: false,
      });
      scrollToTop();
    };
  };

  const test_nothing = getSearchParam("test_nothing"); // test_nothing=1 表示放出 rahu_kaal 菜单栏
  const navItems: NavItem[] = [
    { path: "/home", translationKey: "title33" },
    { path: "/hourly", translationKey: "title34" },
    { path: "/daily", translationKey: "title35" },
    { path: "/sun-moon", translationKey: "title36" },
    ...(isShowNews1().validMain || isShowNews1().isIdVls ? [{ path: "/news", translationKey: "title56" }] : []),
    ...(Number(test_nothing) === 1 ? [{ path: "/rahu-kaal", translationKey: "title65" }] : []),
  ];

  const renderNavItems = () => {
    return navItems
      .filter(item => !item.devOnly || isDevEnv)
      .map(item => {
        const isActive = pathname.indexOf(item.path) === 0 || (pathname === "/home" && item.path === "/now");
        const itemClassName = classNames([
          isActive ? "bg-[var(--app-bg-10)]" : "",
          "inline-block rounded-30  color-[var(--app-text-04)] last-of-type:mr-0 p-[0_10px] mr-8",
        ]);
        const itemStyle = {
          height: `30px`,
          lineHeight: `30px`,
          textDecoration: "none",
        };
        const attachRef = (el: HTMLAnchorElement | HTMLDivElement | null) => {
          navItemRefs.current[item.path] = el;
        };

        if (isMqsHost) {
          const mqsHref = `${window.location.pathname}${window.location.search}#${item.path}`;
          return (
            <a
              key={item.path}
              href={mqsHref}
              onClick={handleClickTab(item.path)}
              className={itemClassName}
              style={itemStyle}
              data-path={item.path}
              ref={attachRef}
            >
              {$t(item.translationKey)}
            </a>
          );
        }

        return (
          <div
            key={item.path}
            onClick={handleClickTab(item.path)}
            className={itemClassName}
            style={itemStyle}
            data-path={item.path}
            ref={attachRef}
          >
            {$t(item.translationKey)}
          </div>
        );
      });
  };
  const id = getSearchParam("id");
  const isNewsDetail = pathname.startsWith("/news/detail") && id;
  const handleGoBack = () => {
    if (history.length <= 2) {
      navigate("/news");
      return;
    }
    navigate(-1);
  };

  if (isEmptyType(homeData.data)) {
    const skeletonStyle = {
      "--width": "100%",
      "--height": pathname.startsWith("/daily/detail") ? "76px" : "50px",
      "--border-radius": "0px",
    };
    return <Skeleton animated style={skeletonStyle} />;
  }

  return (
    <div className={classNames("flex justify-start items-center w-full")} style={bgStyle}>
      {isNewsDetail && (
        <div onClick={handleGoBack} className="w-50 center">
          <SvgIcon className="w-14! h-14!" name={"svg11"} />
        </div>
      )}
      <div
        className={classNames(["flex justify-start items-center", isNewsDetail ? "w-310" : "w-full"])}
        style={navStyle}
      >
        <div
          className={classNames([
            "overflow-x-scroll overflow-y-hidden whitespace-nowrap inline-block w-full text-13 p-[0_10px]",
          ])}
          style={navStyle}
        >
          {renderNavItems()}
        </div>
      </div>
    </div>
  );
};
export default Navigation;
